import type { FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import type { FlowControlSegmentKind } from '@/helpers/simulation-cue-engine.helper';
import { parseInBetweenPlayoutSegmentId } from '@/helpers/flow-control-in-between-playout.helper';
import {
  FLOW_CONTROL_AFTER_FLOW_PLAYOUT_ID,
  FLOW_CONTROL_PRE_FLOW_PLAYOUT_ID,
  FLOW_SLOT_ASSETS_INLINE_FLOW_END_ID,
  FLOW_SLOT_ASSETS_INLINE_FLOW_START_ID,
} from '@/components/flows/detail/slot-list/flow-slot-assets-inline-ids';
import type { SlotSegmentAnchor } from '@/components/flows/detail/slot-list/slot-right-section-play-icon';
import { getSlotAssetsDetailsTitle } from '@/components/flows/detail/edit-details/slot-assets-details-title';

export type FlowControlExpandSegmentMeta = {
  segmentId: string;
  segmentKind: FlowControlSegmentKind;
  segmentAnchor: SlotSegmentAnchor;
  slotId?: string;
  label: string;
  disableManualStop?: boolean;
};

export function resolveFlowControlExpandSegmentLabel(
  expandId: string,
  slot: FlowSlotDto | null | undefined,
  t: (key: string, options?: { defaultValue?: string }) => string,
  slots?: FlowSlotDto[],
): string | null {
  return resolveFlowControlExpandSegmentMeta(expandId, slot, t, slots)?.label ?? null;
}

export function resolveFlowControlExpandSegmentMeta(
  expandId: string,
  slot: FlowSlotDto | null | undefined,
  t: (key: string, options?: { defaultValue?: string }) => string,
  slots?: FlowSlotDto[],
): FlowControlExpandSegmentMeta | null {
  if (!expandId) {
    return null;
  }

  if (expandId === FLOW_CONTROL_PRE_FLOW_PLAYOUT_ID) {
    return {
      segmentId: expandId,
      segmentKind: 'pre_flow',
      segmentAnchor: 'flow_start',
      label: t('flows.detail.control.preFlowPlayout', { defaultValue: 'Pre-flow media playout' }),
    };
  }

  if (expandId === FLOW_SLOT_ASSETS_INLINE_FLOW_START_ID) {
    return {
      segmentId: expandId,
      segmentKind: 'flow_intro',
      segmentAnchor: 'flow_start',
      label: t('flows.detail.playlistMedia.flowIntro', { defaultValue: 'Flow intro media' }),
    };
  }

  if (expandId === FLOW_SLOT_ASSETS_INLINE_FLOW_END_ID) {
    return {
      segmentId: expandId,
      segmentKind: 'flow_outro',
      segmentAnchor: 'flow_end',
      label: t('flows.detail.playlistMedia.flowOutro', { defaultValue: 'Flow outro media' }),
    };
  }

  if (expandId === FLOW_CONTROL_AFTER_FLOW_PLAYOUT_ID) {
    return {
      segmentId: expandId,
      segmentKind: 'after_flow',
      segmentAnchor: 'flow_end',
      label: t('flows.detail.control.afterFlowPlayout', { defaultValue: 'After-flow media playout' }),
    };
  }

  const inBetween = parseInBetweenPlayoutSegmentId(expandId);
  if (inBetween) {
    return {
      segmentId: expandId,
      segmentKind: inBetween.segmentKind,
      segmentAnchor: 'slot',
      slotId: inBetween.slotId,
      label:
        inBetween.segmentKind === 'flow_transition'
          ? t('flows.detail.control.transitionPlayout', { defaultValue: 'Transition playout' })
          : t('flows.detail.control.flowResumePlayout', { defaultValue: 'Flow resume playout' }),
    };
  }

  const resolvedSlot = slot ?? slots?.find((entry) => entry.id === expandId);
  if (resolvedSlot) {
    return {
      segmentId: expandId,
      segmentKind: resolvedSlot.contentType === 'Break' ? 'break' : 'session',
      segmentAnchor: 'slot',
      slotId: resolvedSlot.id,
      label: getSlotAssetsDetailsTitle(resolvedSlot),
    };
  }

  return null;
}
